import type { ReactNode } from 'react';
import { Component } from 'react';
import { createPortal } from 'react-dom';
import { canUseDOM } from 'exenv';

import { getPortalContainer } from './helpers';

interface IProps {
    /** Компонент который будет вынесен в конец DOM у body */
    children: ReactNode;
}

interface IState {
    mounted: boolean;
}

/**
 * Компонент-обертка для [ReactDOM#createPortal](https://reactjs.org/docs/portals.html).
 * Используется в виджетах, которые позиционируются вне нормального потока документа: модальные окна, тултипы, попапы и т. д.
 */
export class Portal extends Component<IProps, IState> {
    state = {
        /**
         * Определяет смонтирован ли компонент. Проверка нужна для [ReactDOM#hydrate](https://reactjs.org/docs/react-dom.html#hydrate).
         * Подробнее: https://github.com/facebook/react/issues/13097#issuecomment-405658104
         */
        mounted: false,
    };

    el?: HTMLDivElement;

    constructor(props: IProps) {
        super(props);

        if (!canUseDOM) {
            return;
        }

        const portalContainer = getPortalContainer();

        this.el = document.createElement('div');
        portalContainer.appendChild(this.el);
    }

    componentDidMount() {
        this.setState({
            mounted: true,
        });
    }

    componentWillUnmount() {
        const portalContainer = getPortalContainer();

        if (this.el) {
            portalContainer.removeChild(this.el);
        }
    }

    render(): ReactNode {
        const { mounted } = this.state;
        const { children } = this.props;

        if (mounted && this.el) {
            return createPortal(children, this.el);
        }

        return null;
    }
}
